<template>
    <div
        v-show="baseImage.src"
        class="l-ss-image-container">
        <svg
            :class="{ 'preview-layer-mobile': $screen.isMobile }"
            class="preview-layer"
            v-show="image.contours">
            <defs>
                <filter id="blur">
                    <feGaussianBlur stdDeviation="6"/>
                </filter>
            </defs>
            <image
                :height="layerSize.height"
                :href="image.src"
                :width="layerSize.width"
                filter="url(#blur)"
                opacity="0.3"/>
        </svg>
      
        <div
            class="image-layer">
            <cropper
                :canvas="{ maxHeight: 350, maxWidth: 500 }"
                :resize-image="false"
                :src="baseImage.src"
                :stencil-props="cropperStencilProps"
                :default-size="cropperDefaultSize"
                ref="cropper"
                @ready="onCropperReady"
                @change="onCropChange"/>
                
            <div
                v-for="(c, index) in getCircleCoordinates"
                :key="index"
                :class="{ 'circle--active': index === activeIndex }"
                :style="{ top: c.top, left: c.left }"
                class="circle"
                @click="onCircleClick(index)"
                @mouseenter="activeIndex = index"
                @mouseleave="!clickedIndex ? activeIndex = null : ''">
            </div>
            <div v-if="getEqualsActiveAndClicked">
                <transition name="scale">
                    <div
                        v-if="getActiveSquareStyles"
                        :style="getActiveSquareStyles"
                        class="square">
                        <div
                            v-if="getActiveName"
                            class="square-product-name">
                            {{ getActiveName }}
                        </div>
                    </div>
                </transition>
            </div>
            <div
                v-if="isShowSquare"
                :style="getClickedSquareStyles"
                class="square">
                <div
                    v-if="getClickedName"
                    class="square-product-name">
                    {{ getClickedName }}
                </div>
            </div>
        </div>
        <div
            v-if="isShowTooltip"
            class="tooltip">
            <span>{{ $t('imageSearch.selectProduct') }}</span>
        </div>
    </div>
</template>

<script>
    export default {
        name: 'section-image',
        components: {
            Cropper: () => import('vue-advanced-cropper').then(({ Cropper }) => Cropper),
        },
        props: {
            image: {
                type: Object,
                required: true,
            },
            hasTooltip: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                getImageSize: null,
                getSquareCoordinates: [],
                activeIndex: null,
                clickedIndex: null,
                layerSize: {
                    width: 0,
                    height: 0,
                },
                layerSizeMultiplier: 3,
                halfCircleRadius: 10,
                active: false,
                cropperStencilProps: {
                    handlers: {
                        eastNorth: true,
                        north: false,
                        westNorth: true,
                        west: false,
                        westSouth: true,
                        south: false,
                        eastSouth: true,
                        east: false,
                    },
                },
                isCropperChanged: false,
                baseImage: {
                    contours: [],
                    src: '',
                },
                baseImageLoaded: false,
                cropperDefaultSize: {
                    width: 500,
                    height: 350,
                },
            };
        },
        computed: {
            hasClicked() {
                return Number.isInteger(this.clickedIndex);
            },
            getEqualsActiveAndClicked() {
                return !this.hasClicked || (this.activeIndex !== this.clickedIndex);
            },
            getCircleCoordinates() {
                if (this.getSquareCoordinates.length) {
                    return this.getSquareCoordinates.map((el) => {
                        return {
                            left: Math.round((el.width / 2 + el.left) - this.halfCircleRadius) + 'px',
                            top: Math.round((el.height / 2 + el.top) - this.halfCircleRadius) + 'px',
                        };
                    });
                }
                return [];
            },

            getClickedSquareStyles() {
                if (this.clickedIndex !== null) {
                    const activeSquare = this.getSquareCoordinates[this.clickedIndex];
                    return {
                        left: activeSquare['left'] + 'px',
                        top: activeSquare['top'] + 'px',
                        width: activeSquare['width'] + 'px',
                        height: activeSquare['height'] + 'px',
                    };
                }
                return null;
            },

            getActiveSquareStyles() {
                if (this.activeIndex !== null) {
                    const activeSquare = this.getSquareCoordinates[this.activeIndex];
                    return {
                        left: activeSquare['left'] + 'px',
                        top: activeSquare['top'] + 'px',
                        width: activeSquare['width'] + 'px',
                        height: activeSquare['height'] + 'px',
                    };
                }
                return null;
            },

            getActiveName() {
                return this.getSquareCoordinates[this.activeIndex].name;
            },
            getClickedName() {
                return this.getSquareCoordinates[this.clickedIndex].name;
            },
            isShowSquare() {
                return this.getClickedSquareStyles;
            },
            isShowTooltip() {
                return this.hasTooltip && this.getCircleCoordinates.length;
            },
        },

        watch: {
            image: {
                deep: true,
                handler(value) {
                    if (value.src && !this.baseImageLoaded) {
                        this.baseImage = JSON.parse(JSON.stringify(value));
                        this.baseImageLoaded = true;
                        if (value.contours) {
                            value.contours.forEach((el) => {
                                this.getSquareCoordinates.push({
                                    name: el.name,
                                    vectors: [],
                                    left: el.rect[0],
                                    top: el.rect[1],
                                    width: el.rect[2] - el.rect[0],
                                    height: el.rect[3] - el.rect[1],
                                });
                            });
                        }
                    }
                },
            },
        },
        methods: {
            async onCircleClick(index) {
                this.clickedIndex = index;
                if (this.clickedIndex !== null) {
                    await this.setCropperCoordinates();
                }
                this.$emit('contour-click', this.baseImage['contours'][this.activeIndex]);
            },
            async setCropperCoordinates() {
                const activeSquare = await this.getSquareCoordinates[this.clickedIndex];
                const stencilCoordinates = {
                    left: activeSquare['left'],
                    top: activeSquare['top'],
                    width: activeSquare['width'],
                    height: activeSquare['height'],
                };
                this.$refs.cropper.coordinates = stencilCoordinates;
            },
            onCropperReady() {
                if (this.$refs?.cropper) {
                    this.$nextTick(() => {
                        this.layerSize.width = this.$refs.cropper?.$el?.clientWidth * this.layerSizeMultiplier;
                        this.layerSize.height = this.$refs.cropper?.$el?.clientHeight * this.layerSizeMultiplier;
                    });
                }
            },
            async onCropChange(params) {
                if (!this.isCropperChanged) {
                    this.isCropperChanged = true;
                    return;
                }
                this.clickedIndex = null;
                this.$emit('crop-change', params);
                this.$emit('search');
            },
        },
    };
</script>
